const tryParseRoleId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = parseInt(value.trim(), 10)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const readRoleIdFromNode = (node: unknown): number | null => {
  if (node == null || typeof node !== 'object') return null
  const attrs = (node as { _attributes?: Record<string, unknown> })._attributes
  if (attrs?.id != null) {
    return tryParseRoleId(attrs.id)
  }
  const directId = (node as { id?: unknown }).id
  if (directId != null) {
    return tryParseRoleId(directId)
  }
  return null
}

/** Role ids from parsed action.roles XML (xml-js: roles.role._attributes.id). */
export function extractActionRoleIds(actionRoles: unknown): number[] {
  if (actionRoles == null || typeof actionRoles !== 'object') {
    return []
  }

  const record = actionRoles as Record<string, unknown>
  const roleNodes = record.role
  if (roleNodes == null) {
    return []
  }

  const nodes = Array.isArray(roleNodes) ? roleNodes : [roleNodes]
  const ids: number[] = []
  const seen = new Set<number>()

  for (const node of nodes) {
    const id = readRoleIdFromNode(node)
    if (id != null && !seen.has(id)) {
      seen.add(id)
      ids.push(id)
    }
  }

  return ids
}

/** ViewAccessCacheBean parity: empty action roles → all users; else role id intersection. */
export function mergeUserRoleIds(...sources: readonly (readonly number[])[]): number[] {
  const seen = new Set<number>()
  const result: number[] = []
  for (const source of sources) {
    for (const id of source) {
      if (!seen.has(id)) {
        seen.add(id)
        result.push(id)
      }
    }
  }
  return result
}

/** ViewAccessCacheBean parity: empty action roles → all users; else role id intersection. */
export function hasActionRoleAccess(
  actionRoles: unknown,
  userRoleIds: readonly number[],
): boolean {
  const actionRoleIds = extractActionRoleIds(actionRoles)
  if (actionRoleIds.length === 0) {
    return true
  }
  if (userRoleIds.length === 0) {
    // Client profile/chain may omit role ids; server already enforced form/action access.
    return true
  }
  return userRoleIds.some((id) => actionRoleIds.includes(id))
}
